/**
 * Seeded Random Number Generator for reproducibility
 * 
 * Uses a Linear Congruential Generator (LCG) for deterministic random number generation.
 * This ensures that the same seed produces the same sequence of random numbers,
 * enabling reproducible experiments for thesis work.
 * 
 * Implementation based on the LCG formula:
 *   state = (state * a + c) mod m
 * 
 * Using constants from Numerical Recipes (a=1664525, c=1013904223, m=2^32)
 * which provide good statistical properties.
 */

export interface SeededRNG {
  /**
   * Returns a random number in [0, 1)
   */
  random(): number;
  
  /**
   * Returns a random integer in [0, max)
   */
  randomInt(max: number): number;
  
  /**
   * Returns a random integer in [min, max] (inclusive)
   */
  randomIntRange(min: number, max: number): number;
  
  /**
   * Returns a random boolean
   */
  randomBool(): boolean;
  
  /**
   * Returns true with probability p (0 <= p <= 1)
   */
  randomChance(p: number): boolean;
  
  /**
   * Returns a random element from an array
   */
  randomChoice<T>(array: T[]): T;
  
  /**
   * Shuffles an array in place using Fisher-Yates algorithm
   */
  shuffle<T>(array: T[]): void;
  
  /**
   * Gets the current seed state (for debugging/reproducibility)
   */
  getState(): number;
  
  /**
   * Creates a new RNG instance with a derived seed
   * Useful for creating independent RNG streams from a base seed
   */
  derive(offset: number): SeededRNG;
}

/**
 * Creates a seeded random number generator
 * @param seed Initial seed value. If not provided, uses current timestamp.
 *             For reproducibility, always provide a fixed seed.
 */
export function createSeededRNG(seed?: number): SeededRNG {
  // Use provided seed or generate from timestamp
  let state = seed ?? Math.floor(Date.now() % 2147483647);
  
  // Ensure seed is in valid range for LCG
  state = Math.abs(state) % 2147483647;
  if (state === 0) state = 1; // Avoid zero state
  
  const a = 1664525;
  const c = 1013904223;
  const m = 0x100000000; // 2^32
  
  const random = (): number => {
    state = (state * a + c) % m;
    // Convert to [0, 1) range
    return (state >>> 0) / m;
  };
  
  return {
    random,
    
    randomInt: (max: number): number => {
      if (max <= 0) return 0;
      return Math.floor(random() * max);
    },
    
    randomIntRange: (min: number, max: number): number => {
      if (min > max) [min, max] = [max, min];
      return min + Math.floor(random() * (max - min + 1));
    },
    
    randomBool: (): boolean => {
      return random() < 0.5;
    },
    
    randomChance: (p: number): boolean => {
      return random() < p;
    },
    
    randomChoice: <T>(array: T[]): T => {
      if (array.length === 0) {
        throw new Error('Cannot choose from empty array');
      }
      return array[Math.floor(random() * array.length)];
    },
    
    shuffle: <T>(array: T[]): void => {
      // Fisher-Yates shuffle
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
    },
    
    getState: (): number => {
      return state;
    },
    
    derive: (offset: number): SeededRNG => {
      // Create a new RNG with a derived seed
      // Use a hash-like function to combine current state and offset
      const derivedSeed = ((state * 7919 + offset * 1000003) % 2147483647);
      return createSeededRNG(derivedSeed);
    },
  };
}

/**
 * Generates a deterministic seed from a base seed and run number
 * This ensures each run gets a unique but reproducible seed
 */
export function generateRunSeed(baseSeed: number, runNumber: number): number {
  // Use a hash-like function to combine base seed and run number
  // This ensures different runs get different seeds, but same base + run = same seed
  return ((baseSeed * 7919 + runNumber * 1000003) % 2147483647);
}

